// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Add, Ai, Chat, Close } from '@carbon/icons-react';
import { useShellStore } from '../../stores/shellStore';
import ChatFullView from '../chat/ChatFullView';
import AskHeliosChat, { type AskHeliosChatHandle } from '../chat/AskHeliosChat';
import './AskHeliosPanel.css';

/**
 * Right-side "Ask Helios" slide-over. Supersedes the old chat/dashboard
 * ViewModeToggle + full-page ChatFullView + ContextPanel: the chat surface is
 * now a contextual panel that can be opened over any screen.
 *
 * On the HOME screen it uses the structured, advisory Ask Helios (SOP Q&A with
 * confidence + citations via POST /ai/ask-helios). Everywhere else it keeps the
 * existing streaming EMS chat (ChatFullView). Scoped to /home for now — widen
 * once the advisory experience is validated.
 */
export default function AskHeliosPanel() {
  const open = useShellStore(s => s.askHeliosOpen);
  const seed = useShellStore(s => s.askHeliosSeed);
  const close = useShellStore(s => s.closeAskHelios);
  const contextLabel = seed
    ? [seed.asset, seed.id].filter(Boolean).join(' · ')
    : null;
  const { pathname } = useLocation();
  const useAdvisory = pathname === '/home' || pathname.startsWith('/home/');
  const chatRef = useRef<AskHeliosChatHandle>(null);

  // Slide-overs are expected to dismiss on Escape; the scrim and X were the
  // only ways out. Bound above the early return to keep hook order stable.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  if (!open) return null;

  return (
    <>
      <div className="ask-helios-scrim" onClick={close} aria-hidden="true" />
      <aside className="ask-helios-panel" role="dialog" aria-label="Ask Helios">
        <div className="ask-helios-head">
          <Ai size={20} className="ask-helios-icon" />
          <div className="ask-helios-head-text">
            <div className="ask-helios-title">Helios</div>
            <div className="ask-helios-context">
              {contextLabel ? `Context · ${contextLabel}` : 'Ambient · whole site'}
            </div>
          </div>
          {useAdvisory && (
            <button
              type="button"
              className="helios-icon-btn"
              onClick={() => chatRef.current?.newChat()}
              aria-label="New chat"
            >
              <Add size={20} />
            </button>
          )}
          <span className="ask-helios-view-toggle" aria-hidden="true">
            <Chat size={16} />
          </span>
          <button type="button" className="helios-icon-btn" onClick={close} aria-label="Close">
            <Close size={20} />
          </button>
        </div>
        <div className="ask-helios-body">
          {useAdvisory ? <AskHeliosChat ref={chatRef} /> : <ChatFullView />}
        </div>
      </aside>
    </>
  );
}
